import {
    buildPublicUrl,
    buildRequestUrl,
    normalizePage,
    normalizeState,
    readStateFromLocation,
} from './paged-search-surface-state.js';
import { acquirePagedSearchBusy, refreshPagedSearchBusy } from './paged-search-surface-busy.js';
import {
    acquirePaginationPending,
    acquirePaginationSlowFeedback,
} from './pagination-feedback.js';
import { prefersReducedMotion, readCssTimeMs } from './motion-policy.js';
import { cancelAnimations, playAnimation, settleAnimations } from './motion-runner.js';

const initializedRoots = new WeakMap();
let activeDocumentSwap = null;
// Reads the user asked for by typing, submitting, clearing or retrying the filter: only these report a failure.
const USER_FILTER_SOURCES = new Set(['search-input', 'search-submit', 'clear', 'retry']);

export function getPagedSearchSurfaceController(root) {
    if (!(root instanceof HTMLElement)) {
        return undefined;
    }

    return initializedRoots.get(root);
}

export function initPagedSearchSurface(root, options = {}) {
    if (!(root instanceof HTMLElement)) {
        return undefined;
    }

    const existingController = initializedRoots.get(root);
    if (existingController !== undefined) {
        return existingController;
    }

    const {
        windowRef = globalThis.window,
        fetchImpl = globalThis.fetch?.bind(globalThis),
        timerApi = globalThis,
        consoleRef = globalThis.console,
        formSelector = '[data-paged-search-form]',
        inputSelector = '[data-paged-search-input]',
        clearSelector = '[data-paged-search-clear]',
        resultsSelector = '[data-paged-search-results]',
        failureSelector = '[data-paged-search-failure]',
        retrySelector = '[data-paged-search-retry]',
        pageUrlAttribute = 'pagedSearchPageUrl',
        searchParam = 'filter',
        pageParam = 'page',
        debounceMs = 250,
        beforeRequest = null,
        afterDestroy = null,
        afterSwap = null,
        afterSettled = null,
        resolveStateFromResponse = null,
        shouldAnimateSwap = null,
    } = options;

    if (typeof fetchImpl !== 'function' || windowRef === undefined || windowRef === null) {
        return undefined;
    }

    const form = root.querySelector(formSelector);
    const input = root.querySelector(inputSelector);
    const clearButton = root.querySelector(clearSelector);
    const results = root.querySelector(resultsSelector);
    const failure = root.querySelector(failureSelector);
    const retryButton = failure instanceof HTMLElement ? failure.querySelector(retrySelector) : null;
    const pageUrl = String(root.dataset[pageUrlAttribute] ?? '').trim();

    if (
        !(form instanceof HTMLFormElement)
        || !(input instanceof HTMLInputElement)
        || !(results instanceof HTMLElement)
        || pageUrl === ''
    ) {
        return undefined;
    }

    let state = readStateFromLocation(windowRef, searchParam, pageParam);

    const syncSearchControls = () => {
        input.value = state.query;

        if (clearButton instanceof HTMLButtonElement) {
            clearButton.hidden = state.query === '';
        }
    };

    const setLoading = (loading) => {
        results.setAttribute('aria-busy', loading ? 'true' : 'false');
    };

    // `Filter failed. Results unchanged.` with `Retry`, outside the results: the typed text, the results and
    // the URL stay as they were. Hidden by typing and by the next filter read; focus on `Retry` moves to the
    // field first, so it is not dropped with the message.
    const setFilterFailure = (visible) => {
        if (!(failure instanceof HTMLElement)) {
            return;
        }

        if (!visible && failure.contains(root.ownerDocument?.activeElement ?? null)) {
            input.focus();
        }
        failure.hidden = !visible;
    };

    const loader = createPagedSearchLoader({
        root,
        results,
        pageUrl,
        windowRef,
        fetchImpl,
        timerApi,
        consoleRef,
        searchParam,
        pageParam,
        afterSwap,
        beforeRequest,
        afterSettled,
        resolveStateFromResponse,
        shouldAnimateSwap,
        getState: () => state,
        setState: (nextState) => {
            state = nextState;
        },
        syncSearchControls,
        setLoading,
        setFilterFailure,
    });
    const eventBindings = bindPagedSearchSurfaceEvents({
        root,
        form,
        input,
        clearButton,
        retryButton,
        results,
        windowRef,
        timerApi,
        debounceMs,
        pageParam,
        searchParam,
        load: loader.load,
        getState: () => state,
        syncSearchControls,
        setFilterFailure,
    });

    syncSearchControls();
    setLoading(false);

    let isControllerDestroyed = false;
    const controller = {
        destroy() {
            if (isControllerDestroyed) {
                return;
            }

            isControllerDestroyed = true;
            eventBindings.destroy();
            loader.destroy();

            if (initializedRoots.get(root) === controller) {
                initializedRoots.delete(root);
            }

            results.removeAttribute('aria-busy');
            invokeHook(afterDestroy, { root, results }, consoleRef, 'afterDestroy');
        },
        // An external change rereads what the user asked for last: a queued search (the typed input), else
        // the request in flight, else the committed state - never a state the user has already left.
        // Resolves to `swapped`, `failed` or `stale` (superseded or aborted).
        reload(historyMode = 'replace', requestMeta = {}) {
            const nextState = eventBindings.takeQueuedSearch() ?? loader.getPendingState() ?? state;

            return loader.load(nextState, historyMode, true, requestMeta);
        },
    };

    initializedRoots.set(root, controller);

    return controller;
}

function createPagedSearchLoader({
    root,
    results,
    pageUrl,
    windowRef,
    fetchImpl,
    timerApi,
    consoleRef,
    searchParam,
    pageParam,
    beforeRequest,
    afterSwap,
    afterSettled,
    resolveStateFromResponse,
    shouldAnimateSwap,
    getState,
    setState,
    syncSearchControls,
    setLoading,
    setFilterFailure,
}) {
    let activeAbortController = null;
    // The state requested by the request in flight; only that request clears it.
    let pendingRequest = null;
    const activeBusyReleases = new Set();
    let isDestroyed = false;

    const updateHistory = (nextState, mode) => {
        if (mode === 'none') {
            return;
        }

        const nextUrl = buildPublicUrl(windowRef, nextState, searchParam, pageParam);
        const historyState = {
            pagedSearchSurface: true,
            query: nextState.query,
            page: nextState.page,
        };

        if (mode === 'push') {
            windowRef.history?.pushState?.(historyState, '', nextUrl.toString());
            return;
        }

        windowRef.history?.replaceState?.(historyState, '', nextUrl.toString());
    };

    const clearPendingRequest = (abortController) => {
        if (pendingRequest?.abortController === abortController) {
            pendingRequest = null;
        }
    };

    return {
        getPendingState() {
            return pendingRequest?.state ?? null;
        },
        destroy() {
            if (isDestroyed) {
                return;
            }

            isDestroyed = true;

            if (activeAbortController instanceof AbortController) {
                activeAbortController.abort();
                activeAbortController = null;
            }

            activeBusyReleases.forEach((release) => release());
            cancelResultsSwap(results);
        },
        async load(nextState, historyMode = 'replace', force = false, requestMeta = {}) {
            const normalizedState = normalizeState(nextState);
            const currentState = getState();

            if (isDestroyed) {
                return 'stale';
            }

            if (
                !force
                && normalizedState.query === currentState.query
                && normalizedState.page === currentState.page
                && activeAbortController === null
            ) {
                syncSearchControls();
                return 'stale';
            }

            if (activeAbortController instanceof AbortController) {
                activeAbortController.abort();
                cancelResultsSwap(results);
            }

            const abortController = new AbortController();
            requestIdSequence += 1;
            const requestId = requestIdSequence;
            const releaseBusyState = acquirePagedSearchBusy(root, 'request');
            let busyReleased = false;
            const releaseBusy = () => {
                if (busyReleased) {
                    return;
                }

                busyReleased = true;
                activeBusyReleases.delete(releaseBusy);
                releaseBusyState();
            };
            activeBusyReleases.add(releaseBusy);
            activeAbortController = abortController;
            pendingRequest = {
                abortController,
                state: normalizedState,
            };
            setLoading(true);

            const baseContext = {
                feedbackTarget: requestMeta?.feedbackTarget ?? null,
                requestedState: normalizedState,
                requestId,
                source: normalizeRequestSource(requestMeta?.source),
            };
            const userFilter = USER_FILTER_SOURCES.has(baseContext.source);
            if (userFilter) {
                setFilterFailure(false);
            }
            const releasePending = baseContext.source === 'pagination'
                ? acquirePaginationPending(root, baseContext.feedbackTarget)
                : () => {};
            const releaseSlowFeedback = baseContext.source === 'pagination'
                ? acquirePaginationSlowFeedback(
                    baseContext.feedbackTarget?.closest?.('[data-pagination-ajax-url]'),
                    timerApi,
                )
                : () => {};
            let responseState = null;
            let requestFailed = false;
            let commitError = null;

            try {
                invokeHook(beforeRequest, baseContext, consoleRef, 'beforeRequest');

                if (shouldIgnoreCompletedRequest(abortController, activeAbortController, isDestroyed)) {
                    return 'stale';
                }

                const requestUrl = buildRequestUrl(pageUrl, windowRef, normalizedState, searchParam, pageParam);
                const response = await fetchImpl(requestUrl.toString(), {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    signal: abortController.signal,
                });

                if (!response.ok) {
                    throw new Error(`Paged search request failed with status ${response.status}`);
                }

                const html = await response.text();
                if (shouldIgnoreCompletedRequest(abortController, activeAbortController, isDestroyed)) {
                    return 'stale';
                }

                await swapResultsMarkup(results, () => {
                    results.innerHTML = html;
                    refreshPagedSearchBusy(root);

                    responseState = normalizedState;
                    if (typeof resolveStateFromResponse === 'function') {
                        try {
                            responseState = normalizeState(resolveStateFromResponse({
                                html,
                                requestedState: normalizedState,
                                results,
                                root,
                                windowRef,
                            }) ?? normalizedState);
                        } catch (error) {
                            commitError ??= error;
                        }
                    }

                    if (commitError === null) {
                        setState(responseState);
                        clearPendingRequest(abortController);
                        syncSearchControls();
                    }

                    if (commitError === null && typeof afterSwap === 'function') {
                        try {
                            afterSwap({
                                ...baseContext,
                                root,
                                results,
                                state: responseState,
                                windowRef,
                            });
                        } catch (error) {
                            commitError ??= error;
                        }
                    }

                    if (commitError === null) {
                        updateHistory(responseState, historyMode);
                    }

                }, {
                    windowRef,
                    shouldAnimate: invokeHook(
                        shouldAnimateSwap,
                        baseContext,
                        consoleRef,
                        'shouldAnimateSwap',
                    ) === true,
                    canMutate: () => !shouldIgnoreCompletedRequest(abortController, activeAbortController, isDestroyed),
                });

                if (shouldIgnoreCompletedRequest(abortController, activeAbortController, isDestroyed)) {
                    return 'stale';
                }

                if (commitError !== null) {
                    requestFailed = true;
                    logHookError(consoleRef, 'commit', commitError);
                    syncSearchControls();
                    replaceWithPublicUrl(windowRef, responseState, searchParam, pageParam, consoleRef);

                    return 'failed';
                }

                root.dispatchEvent(new CustomEvent('paged-search:swap', {
                    bubbles: true,
                    detail: {
                        state: responseState,
                    },
                }));

                return 'swapped';
            } catch (error) {
                // A superseded or aborted read is silent; a failed one leaves the input, results and URL alone.
                if (shouldIgnoreCompletedRequest(abortController, activeAbortController, isDestroyed)) {
                    return 'stale';
                }

                requestFailed = true;
                consoleRef?.error?.(error);
                if (userFilter) {
                    setFilterFailure(true);
                }

                return 'failed';
            } finally {
                const ownsActiveRequest = activeAbortController === abortController;
                if (ownsActiveRequest) {
                    activeAbortController = null;
                }
                clearPendingRequest(abortController);

                releaseSlowFeedback();
                releasePending();
                releaseBusy();

                if (!isDestroyed && ownsActiveRequest) {
                    setLoading(false);
                }

                invokeHook(afterSettled, {
                    ...baseContext,
                    state: responseState ?? getState(),
                    aborted: abortController.signal.aborted,
                    commitFailed: commitError !== null,
                    failed: requestFailed,
                }, consoleRef, 'afterSettled');
            }
        },
    };
}

function bindPagedSearchSurfaceEvents({
    root,
    form,
    input,
    clearButton,
    retryButton,
    results,
    windowRef,
    timerApi,
    debounceMs,
    pageParam,
    searchParam,
    load,
    getState,
    syncSearchControls,
    setFilterFailure,
}) {
    const timerSetTimeout = typeof timerApi?.setTimeout === 'function'
        ? timerApi.setTimeout.bind(timerApi)
        : globalThis.setTimeout.bind(globalThis);
    const timerClearTimeout = typeof timerApi?.clearTimeout === 'function'
        ? timerApi.clearTimeout.bind(timerApi)
        : globalThis.clearTimeout.bind(globalThis);

    let debounceId = null;

    const cancelQueuedSearch = () => {
        if (debounceId === null) {
            return;
        }

        timerClearTimeout(debounceId);
        debounceId = null;
    };

    const queueSearch = () => {
        cancelQueuedSearch();

        debounceId = timerSetTimeout(() => {
            debounceId = null;
            void load({
                query: input.value,
                page: 1,
            }, 'replace', false, {
                source: 'search-input',
            });
        }, debounceMs);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        cancelQueuedSearch();
        void load({
            query: input.value,
            page: 1,
        }, 'replace', false, {
            source: 'search-submit',
        });
    };

    const handleInput = () => {
        const nextQuery = String(input.value ?? '').trim();
        if (clearButton instanceof HTMLButtonElement) {
            clearButton.hidden = nextQuery === '';
        }

        setFilterFailure(false);
        queueSearch();
    };

    // The failed filter again, with what the field holds now, from its first page.
    // The message goes first, while `Retry` still holds focus: the busy lock of the read disables and blurs it.
    const handleRetry = () => {
        cancelQueuedSearch();
        setFilterFailure(false);
        void load({
            query: input.value,
            page: 1,
        }, 'replace', true, {
            source: 'retry',
        });
    };

    // Cleared here too: back to an unfiltered committed state, the load below has nothing to send.
    const handleClear = () => {
        input.value = '';
        cancelQueuedSearch();
        setFilterFailure(false);

        if (clearButton instanceof HTMLButtonElement) {
            clearButton.hidden = true;
        }

        void load({
            query: '',
            page: 1,
        }, 'replace', false, {
            source: 'clear',
        }).then(() => {
            input.focus();
        });
    };

    const handleResultsClick = (event) => {
        const target = event.target;
        if (target === null || typeof target !== 'object' || typeof target.closest !== 'function') {
            return;
        }

        const paginationLink = target.closest('[data-page-number]');
        if (!(paginationLink instanceof HTMLElement)) {
            return;
        }

        const href = typeof paginationLink.getAttribute === 'function'
            ? paginationLink.getAttribute('href')
            : null;
        if (typeof href !== 'string' || href.trim() === '') {
            return;
        }

        event.preventDefault();

        if (root.dataset.loading === 'true') {
            return;
        }

        const url = new URL(href, windowRef.location?.origin ?? 'http://localhost');
        const nextPage = normalizePage(url.searchParams.get(pageParam) ?? '1');

        void load({
            query: input.value,
            page: nextPage,
        }, 'push', false, {
            feedbackTarget: paginationLink,
            source: 'pagination',
        });
    };

    const handlePopState = () => {
        const nextState = readStateFromLocation(windowRef, searchParam, pageParam);
        const currentState = getState();
        if (nextState.query === currentState.query && nextState.page === currentState.page) {
            syncSearchControls();
            return;
        }

        cancelQueuedSearch();
        input.value = nextState.query;

        if (clearButton instanceof HTMLButtonElement) {
            clearButton.hidden = nextState.query === '';
        }

        void load(nextState, 'none', false, {
            source: 'popstate',
        });
    };

    form.addEventListener('submit', handleSubmit);
    input.addEventListener('input', handleInput);
    input.addEventListener('search', handleInput);
    results.addEventListener('click', handleResultsClick);
    windowRef.addEventListener?.('popstate', handlePopState);

    if (clearButton instanceof HTMLButtonElement) {
        clearButton.addEventListener('click', handleClear);
    }

    if (retryButton instanceof HTMLButtonElement) {
        retryButton.addEventListener('click', handleRetry);
    }

    return {
        // Consumes a search still waiting for its debounce: the timer is cancelled and the caller loads it.
        takeQueuedSearch() {
            if (debounceId === null) {
                return null;
            }

            cancelQueuedSearch();

            return {
                query: input.value,
                page: 1,
            };
        },
        destroy() {
            cancelQueuedSearch();
            form.removeEventListener('submit', handleSubmit);
            input.removeEventListener('input', handleInput);
            input.removeEventListener('search', handleInput);
            results.removeEventListener('click', handleResultsClick);
            windowRef.removeEventListener?.('popstate', handlePopState);

            if (clearButton instanceof HTMLButtonElement) {
                clearButton.removeEventListener('click', handleClear);
            }

            if (retryButton instanceof HTMLButtonElement) {
                retryButton.removeEventListener('click', handleRetry);
            }
        },
    };
}

let requestIdSequence = 0;

function normalizeRequestSource(source) {
    const normalizedSource = typeof source === 'string' ? source.trim() : '';
    return normalizedSource || 'reload';
}

function invokeHook(hook, context, consoleRef, hookName) {
    if (typeof hook !== 'function') {
        return undefined;
    }

    try {
        return hook(context);
    } catch (error) {
        logHookError(consoleRef, hookName, error);

        return undefined;
    }
}

function logHookError(consoleRef, hookName, error) {
    consoleRef?.error?.(`Paged search hook "${hookName}" failed`, error);
}

function replaceWithPublicUrl(windowRef, state, searchParam, pageParam, consoleRef) {
    try {
        const url = buildPublicUrl(windowRef, state, searchParam, pageParam);
        windowRef.location?.replace?.(url.toString());
    } catch (error) {
        consoleRef?.error?.('Paged search recovery navigation failed', error);
    }
}

export async function swapResultsMarkup(results, commit, options = {}) {
    if (!(results instanceof HTMLElement)) {
        return;
    }

    const {
        windowRef = globalThis.window,
        shouldAnimate = false,
        canMutate = () => true,
    } = options;

    if (canMutate() !== true || typeof commit !== 'function') {
        return;
    }

    cancelActiveDocumentSwap();

    const documentRef = results.ownerDocument;
    if (
        shouldAnimate !== true
        || documentRef?.visibilityState === 'hidden'
        || prefersReducedMotion(windowRef) === true
        || typeof documentRef?.startViewTransition !== 'function'
    ) {
        if (canMutate() !== true) {
            return;
        }

        commit();
        return;
    }

    const documentElement = documentRef.documentElement;
    const previousHeight = readElementHeight(results);
    const run = {
        committed: false,
        documentElement,
        heightAnimation: null,
        nextHeight: previousHeight,
        results,
        transition: null,
        visualCancelled: false,
    };

    activeDocumentSwap = run;
    documentElement?.classList?.add('paged-search-transition-active');
    results.classList.add('paged-search-transition-target');
    if (previousHeight > 0) {
        results.style.setProperty('height', `${previousHeight}px`);
    }

    try {
        try {
            run.transition = documentRef.startViewTransition(() => {
                if (run.committed === true || canMutate() !== true) {
                    return;
                }

                if (run.visualCancelled) {
                    commit();
                    run.committed = true;
                    return;
                }

                results.style.removeProperty('height');
                commit();
                run.committed = true;
                run.nextHeight = readElementHeight(results);
                if (run.nextHeight > 0) {
                    results.style.setProperty('height', `${run.nextHeight}px`);
                }
            });
        } catch (_error) {
            if (canMutate() === true) {
                results.style.removeProperty('height');
                commit();
                run.committed = true;
            }

            return;
        }

        if (
            typeof run.transition?.ready?.then !== 'function'
            || typeof run.transition?.finished?.then !== 'function'
        ) {
            if (run.committed !== true && canMutate() === true) {
                results.style.removeProperty('height');
                commit();
                run.committed = true;
            }

            return;
        }

        try {
            await run.transition.ready;
        } catch (_error) {
            await run.transition.finished;
            return;
        }

        if (activeDocumentSwap !== run || run.committed !== true || canMutate() !== true) {
            return;
        }

        const duration = readCssTimeMs(results, '--paged-search-swap-duration', 180);
        results.classList.add('paged-search-transition-layout');
        if (previousHeight > 0 && run.nextHeight > 0 && previousHeight !== run.nextHeight) {
            run.heightAnimation = playAnimation(results, [
                { height: `${previousHeight}px` },
                { height: `${run.nextHeight}px` },
            ], {
                duration,
                easing: 'ease',
            });
        }

        await Promise.all([
            run.transition.finished,
            settleAnimations([run.heightAnimation]),
        ]);
    } finally {
        cleanupDocumentSwap(run);
    }
}

function shouldIgnoreCompletedRequest(abortController, activeAbortController, isDestroyed) {
    return isDestroyed === true
        || abortController.signal.aborted === true
        || activeAbortController !== abortController;
}

function cancelActiveDocumentSwap() {
    const run = activeDocumentSwap;
    if (run === null) {
        return;
    }

    run.visualCancelled = true;
    run.transition?.skipTransition?.();
    cancelAnimations([run.heightAnimation]);
    cleanupDocumentSwap(run);
}

function cancelResultsSwap(results) {
    if (activeDocumentSwap?.results !== results) {
        return;
    }

    cancelActiveDocumentSwap();
}

function cleanupDocumentSwap(run) {
    if (activeDocumentSwap !== run) {
        return;
    }

    run.results.style.removeProperty('height');
    run.results.classList.remove('paged-search-transition-layout', 'paged-search-transition-target');
    run.documentElement?.classList?.remove('paged-search-transition-active');
    activeDocumentSwap = null;
}

function readElementHeight(element) {
    if (!(element instanceof HTMLElement) || typeof element.getBoundingClientRect !== 'function') {
        return 0;
    }

    const rect = element.getBoundingClientRect();
    const height = Number.isFinite(rect?.height) ? rect.height : 0;

    return height > 0 ? Math.ceil(height) : 0;
}
